(function () {
  // `detectedCountryPromise` is memoized at this (module/IIFE) scope, not inside
  // `getDetectedCountry` - so repeated calls within the same page load reuse the same
  // in-flight/resolved promise instead of re-fetching every time.
  var detectedCountryPromise = null;

  // Persists the last CONFIRMED country we tracked a "Country Changed" event for, so we only
  // fire once per actual change (not on every single page load for a returning visitor whose
  // country hasn't moved). Restores the pre-rewrite behavior (see git history, commit
  // c16edc72), just driven by the GeoIP-detected country below instead of the old
  // window.currentSessionCountry (the shop's selected market/localization country, which
  // doesn't reflect the visitor's real location/currency the same way).
  var STORAGE_KEY = 'klaviyo_last_country';

  // A detected country only gets CONFIRMED (and the "Country Changed" event fired) once it has
  // held steady across page loads for this long. Without this, a visitor whose GeoIP detection
  // flaps between two countries in the same sitting (VPN, flaky network-edge routing, a
  // borderline/ambiguous IP, etc.) would fire a "Country Changed" event - and trigger whatever
  // Klaviyo flow/email is wired to it - every single time it flipped, potentially emailing the
  // same person multiple times in one sitting.
  // `klaviyo_pending_country`/`klaviyo_pending_since` track a candidate new country and when it
  // was first seen; the candidate resets its clock (not just its value) every time detection
  // reports a DIFFERENT country than the current candidate, so only a country that's actually
  // stuck for the full window gets confirmed. The very first-ever detection for a browser (no
  // confirmed country yet) is not gated - there's no prior country for it to have "changed"
  // from, so it tracks immediately like before.
  var PENDING_COUNTRY_KEY = 'klaviyo_pending_country';
  var PENDING_SINCE_KEY = 'klaviyo_pending_since';
  var CONFIRM_WINDOW_MS = 60 * 60 * 1000; // 1 hour

  // Real GeoIP/session-based country (from Shopify's browsing-context detection), not the
  // shop's currently-selected market/localization country and not shipping/billing address.
  // Falls back to null (caller uses `window.currentSessionCountry` instead) if detection
  // isn't available or the fetch fails.
  function getDetectedCountry() {
    if (!detectedCountryPromise) {
      detectedCountryPromise = (async () => {
        var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        var response = await fetch(root + 'browsing_context_suggestions.json?country%5Benabled%5D=true');
        if (!response.ok) return null;

        var data = await response.json();
        var detected = data && data.detected_values && data.detected_values.country;
        if (!detected || !detected.handle) return null;

        return { code: detected.handle, name: detected.name || data.detected_values.country_name };
      })().catch(function () {
        return null; // keep the Liquid-rendered market country on failure
      });
    }
    return detectedCountryPromise;
  }

  window.loadScriptWithConsent('analytics', function () {
    getDetectedCountry().then(function (detected) {
      var country = (detected && detected.name) || window.currentSessionCountryName;
      if (!country) return;

      window._learnq = window._learnq || [];
      window._learnq.push(['identify', { 'Last Session Country': country }]);

      // Fire a separate "Country Changed" event whenever the detected country differs from
      // the last CONFIRMED one for this browser. The very first time we ever see this visitor
      // (lastCountry is null - nothing to have "changed" from yet) still tracks immediately,
      // same as the original behavior. Any SUBSEQUENT change only fires once `country` has
      // been the standing candidate for CONFIRM_WINDOW_MS - see the comment by that constant.
      var lastCountry = null;
      var pendingCountry = null;
      var pendingSince = 0;
      try {
        lastCountry = window.localStorage.getItem(STORAGE_KEY);
        pendingCountry = window.localStorage.getItem(PENDING_COUNTRY_KEY);
        pendingSince = parseInt(window.localStorage.getItem(PENDING_SINCE_KEY), 10) || 0;
      } catch (e) {
        return;
      }

      if (lastCountry === country) {
        // Back to the already-confirmed country (e.g. a flap that reverted) - drop any
        // pending candidate so a later flap doesn't get to "inherit" this stale clock.
        if (pendingCountry !== null) {
          try {
            window.localStorage.removeItem(PENDING_COUNTRY_KEY);
            window.localStorage.removeItem(PENDING_SINCE_KEY);
          } catch (e) {}
        }
        return;
      }

      if (lastCountry !== null) {
        // A real change candidate (not the first-ever detection) - gate it behind the
        // confirmation window instead of firing immediately.
        if (pendingCountry !== country) {
          // First time we've seen this candidate (or it changed again since the last page
          // load) - start/restart its confirmation clock. Do not fire yet.
          try {
            window.localStorage.setItem(PENDING_COUNTRY_KEY, country);
            window.localStorage.setItem(PENDING_SINCE_KEY, String(Date.now()));
          } catch (e) {}
          return;
        }

        if (Date.now() - pendingSince < CONFIRM_WINDOW_MS) {
          // Same candidate as before, but hasn't held steady for the full window yet.
          return;
        }
      }

      // Either the first-ever detection, or a candidate that has held steady for
      // CONFIRM_WINDOW_MS - confirm the change.
      window._learnq.push(['track', 'Country Changed', {
        'Previous Country': lastCountry,
        'New Country': country
      }]);

      try {
        window.localStorage.setItem(STORAGE_KEY, country);
        window.localStorage.removeItem(PENDING_COUNTRY_KEY);
        window.localStorage.removeItem(PENDING_SINCE_KEY);
      } catch (e) {}
    });
  });
})();
